import React, { useState } from "react";

type Option = { label: string; value: string };

type DropdownFieldProps = {
  options: Option[];
  fontSize?: string | number;
  fontWeight?: string | number;
  lineHeight?: string | number;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  placeholder?: string;
  disabled?: boolean;
  width?: string | number;
  style?: React.CSSProperties;
  error?: boolean;
};

const DropdownField: React.FC<DropdownFieldProps> = ({
  options,
  fontSize = "16px",
  fontWeight = 400,
  lineHeight = "20px",
  value,
  onChange,
  placeholder,
  disabled,
  width = "352px",
  style = {},
  error = false,
}) => {
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);

  // Wrapper
  const wrapperStyle: React.CSSProperties = {
    position: "relative",
    width,
    maxWidth: "100%",
    minWidth: 0,
    ...style,
  };

  // Base select style, matching .input-field
  const selectStyle: React.CSSProperties = {
    fontFamily: "'Montserrat', sans-serif",
    fontSize,
    fontWeight,
    lineHeight,
    width: "100%",
    height: "48px",
    padding: "0 44px 0 16px", // space left+chevron right
    boxSizing: "border-box",
    border: "1px solid #e3e3e3",
    borderRadius: "24px",
    backgroundColor: "#ffffff",
    boxShadow: "0px 1px 2px rgba(0,0,0,0.08)",
    outline: "none",
    transition: "border 0.2s ease, box-shadow 0.2s ease",
    appearance: "none",
    WebkitAppearance: "none",
    MozAppearance: "none",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    cursor: disabled ? "not-allowed" : "pointer",
  };

  // Hover
  if (hovered && !focused && !error) {
    selectStyle.borderColor = "#b3b3b3";
  }

  // Focus
  if (focused && !error) {
    selectStyle.borderColor = "var(--primary)";
    selectStyle.boxShadow = "0 0 0 2px rgba(139, 44, 215, 0.25)";
  }

  // Error
  if (error) {
    selectStyle.borderColor = "red";
    selectStyle.boxShadow = "0 0 0 2px rgba(255, 0, 0, 0.2)";
  }

  // Chevron
  const chevronStyle: React.CSSProperties = {
    position: "absolute",
    right: "20px",
    top: "50%",
    transform: "translateY(-50%)",
    pointerEvents: "none",
    color: "#555",
  };

  return (
    <div style={wrapperStyle}>
      <select
        style={selectStyle}
        value={value}
        onChange={onChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        disabled={disabled}
        aria-invalid={error || undefined}
        title={
          options.find((o) => o.value === value)?.label ??
          (placeholder ? String(placeholder) : "")
        }
      >
        {placeholder ? (
          <option value="" disabled hidden>
            {placeholder}
          </option>
        ) : null}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value} title={opt.label}>
            {opt.label}
          </option>
        ))}
      </select>

      {/* Chevron */}
      <svg
        style={chevronStyle}
        width="16"
        height="16"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          fill="currentColor"
          d="M7.41 8.58L12 13.17l4.59-4.59L18 10l-6 6-6-6z"
        />
      </svg>
    </div>
  );
};

export default DropdownField;
